/* Copyright (c) 2020-present CloudIO Inc */

import { getIn, removeIn, setIn } from '@cloudio-saas/mutx';
import {
  FormatColumnValueFunction,
  FormatColumnValueFunctionResult,
  IconValueWithToolTip,
} from 'components/CanvasGrid/cells/types';
import { D3Props, uid } from 'components/D3Chart/uid';
import { IOComponentProps } from 'components/Page/PageCSS';
import { BaseFunctionProps } from 'controller/Controller';
import {
  autoPopulateChildren,
  autoPopulateChildrenAction,
} from 'designer/state';
import { useAnimationFrame } from 'framer-motion';
import { AppHeaderComponentProps } from 'layout/Header/Header';
import { isWhoAttribute } from 'layout/Sidebar/atoms';
import { RunUIScriptProps } from 'layout/Sidebar/panels/DataSourceView/constants';
import {
  camelCase,
  capitalize,
  groupBy,
  kebabCase,
  snakeCase,
  sortBy,
  startCase,
  throttle,
  upperFirst,
} from 'lodash-es';
import { createItem } from 'registry/Components';
import { recordId } from 'store/utils/utils';
import { useRowIndex } from './common/Context';
import { defaultCommonProps } from './componentregistry/layout/Sidebar/panels/PropertiesView/CommonProperties';
import { Platform } from './componentregistry/platform/Platform';
import {
  ActionItemType,
  ItemType,
  TriggerItemType,
} from './componentregistry/registry/Base.type';
import {
  BaseProps,
  BooleanExpressionValue,
  ComponentManager,
  ComponentManagerFn,
  DefaultExpressionValue,
  ExpressionType,
  ExpressionValue,
  Item,
  ItemData,
  ItemID,
  Manager,
  PropertyContext,
} from './componentregistry/registry/Component.type';
import { AnyItem, Page } from './componentregistry/registry/ComponentRegistry';
import { baseComponentManager } from './componentregistry/registry/Components';
import { toOptions } from './componentregistry/utils/ManagerUtils';
import {
  useAttribute,
  useAttributeDirty,
  useBooleanAttribute,
  useCurrentDataStore,
  useIsEditable,
  useNumberAttribute,
  useRecordsOrder,
  useSelectedRecordsOrder,
  useSetCurrentRecord,
  useStoreAttribute,
  useStoreCurrentRecordAttribute,
  useStoreRecords,
  useStoreRecordsOrder,
  useStoreSelectedRecordsOrder,
  useStringAttribute,
} from './components/DataStoreReference/DataStoreReference';
import { usePopover } from './components/Popover/usePopover';
import { useParentProperty } from './components/TabItem/useTabStyles';
import { UserProfilePic } from './components/UserPic/UserPic';
import { useUserProfile } from './components/UserPic/UserProfileCache';
import { getUrl } from './config/config';
import {
  useAsyncControllerFunction,
  useSyncControllerFunction,
} from './controller/ControllerRegistry';
import { useDeveloperMode, designPath } from './designer/atoms';
import { useIORef } from './designer/useIORef';
import {
  useBooleanExpression,
  useExpression,
  useNumberExpression,
  useStringExpression,
} from './expression/useExpression';
import useBorder from './hooks/useBorder';
import useComponentSize from './hooks/useComponentSize';
import useConstant from './hooks/useConstant';
import useDebounce from './hooks/useDebounce';
import useIOActive from './hooks/useIOActive';
import useMargin from './hooks/useMargin';
import {
  useMarginPadding,
  useMarginPaddingBorder,
  usePaddingBorder,
} from './hooks/useMarginPadding';
import useOnScreen from './hooks/useOnScreen';
import usePadding from './hooks/usePadding';
import useScreenSize, { Size } from './hooks/useScreenSize';
import useSubscription from './hooks/useSubscription';
import useWhyDidYouUpdate from './hooks/useWhyDidYouUpdate';
import useWindowSize from './hooks/useWindowSize';
import Icon from './icons/Icon';
import {
  FnWithResult,
  FnWithResults,
  MultiPostFunction,
  SQLResult,
  SinglePostFunction,
  TestFn,
  TestResult,
  UIDriver,
} from './layout/Sidebar/panels/TestsView/types';
import { extractDevProps } from './platform/DevProps';
import { usePending, usePlatform } from './platform/PlatformHooks';
import useUser, {
  getAppHomePage,
  getProfileValue,
  getSessionProperty,
  getUser,
  hasRole,
  userAtom,
} from './root/useUser';
import Store from './store/Store';
import { Records, isFromDB } from './store/storeTypes';
import {
  useRecord,
  useStoreAttributeSortState,
  useStoreBusy,
  useStoreCurrentRecord,
  useStoreCurrentRecordId,
  useStoreCurrentRecordIdCallback,
  useStoreDirty,
} from './store/useAppStore';
import useDataStore, { getStoreCreateIfNew } from './store/useDataStore';
import { useTheme } from './theme/Theme';
import { getColor, useThemeColor } from './theme/useThemeColor';
import useTrigger from './triggers/useTrigger';
import NoDataFound from './utils/NoDataFound';
import WaveDots from './utils/WaveDots';
import { isEmpty, isNotEmpty, isNull } from './utils/isEmpty';
import { useTranslate } from './utils/lang';
import { getURLQueryStringToPost } from './utils/url';
import useJoinRef from 'utils/useJoinRef';
import {
  executeAction,
  useFetch,
} from 'layout/Sidebar/panels/SQLWorksheet/state';
import DSAttributesCache, {
  attributesPath,
  pkAttributesPath,
} from 'store/DSAttributesCache';

const initializeAttributes = DSAttributesCache.setInitializedAttributes;

export type {
  ActionItemType,
  AppHeaderComponentProps,
  AnyItem,
  BaseFunctionProps,
  BaseProps,
  BooleanExpressionValue,
  ComponentManager,
  ComponentManagerFn,
  D3Props,
  ExpressionType,
  ExpressionValue,
  FnWithResult,
  FnWithResults,
  FormatColumnValueFunction,
  FormatColumnValueFunctionResult,
  IOComponentProps,
  IconValueWithToolTip,
  Item,
  ItemData,
  ItemID,
  ItemType,
  Manager,
  MultiPostFunction,
  Page,
  Platform,
  PropertyContext,
  Records,
  RunUIScriptProps,
  SQLResult,
  SinglePostFunction,
  Size,
  TestFn,
  TestResult,
  TriggerItemType,
  UIDriver,
};
export {
  attributesPath,
  pkAttributesPath,
  initializeAttributes,
  userAtom,
  getUser,
  useFetch,
  executeAction,
  autoPopulateChildrenAction,
  DefaultExpressionValue,
  Icon,
  NoDataFound,
  Store,
  UserProfilePic,
  WaveDots,
  autoPopulateChildren,
  baseComponentManager,
  createItem,
  defaultCommonProps,
  extractDevProps,
  getAppHomePage,
  getColor,
  getIn,
  getProfileValue,
  getSessionProperty,
  getStoreCreateIfNew,
  getURLQueryStringToPost,
  getUrl,
  hasRole,
  isEmpty,
  isFromDB,
  camelCase,
  capitalize,
  groupBy,
  kebabCase,
  snakeCase,
  sortBy,
  startCase,
  throttle,
  upperFirst,
  isNotEmpty,
  isNull,
  isWhoAttribute,
  recordId,
  removeIn,
  setIn,
  toOptions,
  uid as d3uid,
  useAnimationFrame,
  useAsyncControllerFunction,
  useAttribute,
  useAttributeDirty,
  useBooleanAttribute,
  useBooleanExpression,
  useBorder,
  useComponentSize,
  useConstant,
  useCurrentDataStore,
  useDataStore,
  useDebounce,
  designPath,
  useDeveloperMode,
  useExpression,
  useIOActive,
  useIORef,
  useIsEditable,
  useJoinRef,
  useMargin,
  useMarginPadding,
  useMarginPaddingBorder,
  useNumberAttribute,
  useNumberExpression,
  useOnScreen,
  usePadding,
  usePaddingBorder,
  useParentProperty,
  usePending,
  usePlatform,
  usePopover,
  useRecord,
  useRecordsOrder,
  useRowIndex,
  useScreenSize,
  useSelectedRecordsOrder,
  useSetCurrentRecord,
  useStoreAttribute,
  useStoreAttributeSortState,
  useStoreBusy,
  useStoreCurrentRecord,
  useStoreCurrentRecordAttribute,
  useStoreCurrentRecordId,
  useStoreCurrentRecordIdCallback,
  useStoreDirty,
  useStoreRecords,
  useStoreRecordsOrder,
  useStoreSelectedRecordsOrder,
  useStringAttribute,
  useStringExpression,
  useSubscription,
  useSyncControllerFunction,
  useTheme,
  useThemeColor,
  useTranslate,
  useTrigger,
  useUser,
  useUserProfile,
  useWhyDidYouUpdate,
  useWindowSize,
};
